"use client";

import { useState } from "react";
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";

/**
 * Global Toaster — top-right, liquid-glass styling.
 *
 * Default sonner toasts (toast.success / .error / .info / .loading)
 * inherit a frosted backdrop + variant-tinted border via classNames.
 * Custom action toasts rendered through `glassToast` use their own
 * full LiquidGlassCard treatment via toast.custom.
 *
 * Theming is token-driven so every product/theme skins it automatically:
 * card surface + primary action follow the semantic tokens, which resolve
 * per product (html[data-product]) and per scheme (light-dark()).
 *
 * Stacking: sonner collapses the stack and expands it on hover (OSX-style).
 * We also pin `expand` open on click/tap so touch devices — which have no
 * hover — can open the stack, and let it re-collapse when the pointer leaves.
 */
export function Toaster() {
  const [expanded, setExpanded] = useState(false);
  const { resolvedTheme } = useTheme();

  return (
    // Toasts are fixed-positioned descendants of this wrapper, so clicks/leaves
    // anywhere over the stack bubble here via the DOM tree.
    <div
      onClick={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <Sonner
        data-slot="toaster"
        position="top-right"
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        expand={expanded}
        // On mobile the top-right anchor sits over the h-14 app header (the only
        // nav affordance). Push toasts below it so they never bury the hamburger.
        mobileOffset={{ top: "4.5rem", right: "1rem", left: "1rem" }}
        toastOptions={{
          unstyled: false,
          classNames: {
            // Generated Sonner toasts are adopted by the global liquid-glass
            // controller through Sonner's data attributes. Keep only spacing,
            // shape, and typography here; the material owns the optics.
            // Scoped to data-styled=true: sonner marks toast.custom toasts
            // data-styled=false, and glassToast brings its own LiquidGlassCard
            // — applying this chrome there double-wraps the card.
            toast:
              "data-[styled=true]:!border data-[styled=true]:!text-card-foreground data-[styled=true]:!rounded-2xl data-[styled=true]:!p-4 data-[styled=true]:!gap-3",
            title:
              "!text-card-foreground !font-semibold !text-2sm !leading-snug",
            description: "!text-muted-foreground !text-2sm !leading-snug",
            actionButton:
              "!bg-primary !text-primary-foreground !rounded-full !px-3.5 !py-1.5 !text-xs !font-medium !shadow-sm !shadow-black/10 hover:!opacity-90 !transition-opacity",
            cancelButton:
              "!bg-secondary !text-secondary-foreground !rounded-full",
            success: "!border-l-2 !border-l-primary",
            error: "!border-l-2 !border-l-destructive",
            info: "!border-l-2 !border-l-info",
            warning: "!border-l-2 !border-l-warning",
            loading: "!border-l-2 !border-l-info",
          },
        }}
      />
    </div>
  );
}
